import { Component, OnDestroy, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Package} from '@app/modules/shared/models';
import {select, Store} from '@ngrx/store';
import Swal from 'sweetalert2';
import {ToastrService} from 'ngx-toastr';

import {
  getPackagesPaginated,
  PackagesListPaginated,
  ResetPackageState,
  IPackageState, PackagesDelete, getPackageError, getPackageSuccess,
} from '@app/stores';

import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { EntityLinkDirective } from '../../../_components/unified/entity-form-modal/entity-link.directive';
import { RouterLink } from '@angular/router';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { SbTableComponent } from '@app/_components/unified/sb-table/sb-table.component';
import { SbTableActionsDirective, SbTableCellDirective } from '@app/_components/unified/sb-table/sb-table.directives';
import { SbTableColumn, SbTableOptions, SbTableServerState } from '@app/_components/unified/sb-table/sb-table.types';
@Component({
  selector: 'app-package-list',
  templateUrl: './package-list.component.html',
  styleUrls: ['./package-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PageShellComponent, EntityLinkDirective, RouterLink, SbTableComponent, SbTableCellDirective, SbTableActionsDirective,
    MatMenuTrigger, MatMenu]
})
export class PackageListComponent implements OnDestroy {
  private packageStore = inject<Store<IPackageState>>(Store);
  private toastrService = inject(ToastrService);

  unsubscriber = new Subject();
  readonly packages = signal<Package[]>([]);
  readonly totalPackages = signal(0);
  deleteAction = false;

  // The list does not sort, so the ids are only names for the cell templates.
  readonly tableColumns: SbTableColumn[] = [
    {id: 'name', header: 'Name'},
    {id: 'project_count_limit', header: 'Project Limit'},
    {id: 'project_campaign_count_limit', header: 'Campaign Limit'},
    {id: 'project_influencer_count_limit', header: 'Influencer Limit'},
  ];
  readonly tableOptions: SbTableOptions = {
    serverSide: true,
    urlState: true,
    ordering: false,
    pageLength: 10,
    lengthMenu: [10, 25, 50, 100],
    selectable: false,
  };


  constructor() {
    this.packageStore.dispatch(ResetPackageState({params: {}}));
    this.subscribeStores();
  }

  subscribeStores() {
    this.packageStore.pipe(select(getPackageError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastrService.error(error);
        }
      });
    this.packageStore.pipe(select(getPackageSuccess))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(success => {
        if (success) {
          if (this.deleteAction) {
            this.deleteAction = false;
            this.loadAllPackages();
          }
        }
      });
    this.packageStore.pipe(select(getPackagesPaginated))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((paginatedPackages: any) => {
        if (paginatedPackages) {
          this.packages.set(paginatedPackages.list);
          this.totalPackages.set(paginatedPackages.paging.total);
        }
      }
      );
  }

  /** The table asks for a page: the request the list has always sent for it. */
  onTableState(state: SbTableServerState) {
    this.loadAllPackages(state.page - 1, state.pageLength, state.search);
  }

  private loadAllPackages(page = 0, perPage = 10, search = '') {
    this.packageStore.dispatch(PackagesListPaginated({
      params: {
        options: JSON.stringify({includePagination: true}),
        page: page + 1,
        perPage,
        search
      }
    }));
  }

  deletePackage(packageId: number) {
    Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    }).then(result => {
      if (result.value) {
        this.deleteAction = true;
        this.packageStore.dispatch(PackagesDelete({packageId}));
      }
    });
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }

}
